// The org's recipes, read back from the trace store into a browser cache.
//
// Every recipe a chat keeps is a save_recipe call whose result carries the
// recipe whole, and the conversation search index holds that result as a
// tool_result part of the chat's own trace. The analytics gateway allows a
// fleet-wide search of that index (one LIKE term plus equality predicates,
// a window of at most thirty days, at most two hundred rows), while the
// tool-call entity itself is trace-bound and its argument and content
// fields are not queryable. So the org's saves are found by searching the
// index for save_recipe results, newest first, capped; a hit's text is the
// result when the part fits one search chunk, and the part is read whole
// through the content API when it does not. (The org's uses of a recipe
// would come from the same search over run_recipe calls, but that search
// does not return inside the gateway's deadline; verdicts are local.)
//
// Bounded by min(since the last sync, a cap): a fresh browser reads the
// last week's saves up to the cap; a returning one reads since its last
// sync, less a slack for chats that were still open, up to the same cap.
// Each sync then also reads one more week further back than the cache has
// covered, until a month is, so a new browser reaches the org's older
// recipes in a few syncs. The scan costs about the same whatever the window
// (the search view is not pruned by time) and the gateway cancels it near
// twenty-five seconds, so a sync runs in the background, at most once an
// hour per browser, and never holds a chat up: the chat reads the cache and
// the block in its prompt is refreshed when a sync lands.
//
// The org learns the same plan more than once: chats on different browsers
// distil the same shape before either has the other's copy. The cache keeps
// every save by id but lists one recipe per plan signature, the newest save,
// and says how many variants it stands for.
//
// The gateway clients are loaded when a sync runs, not when this module is:
// the recipe store imports this module, and the store is used where no
// browser exists.
import { browserStorageKey } from "./browserStorageScope.js";
import { savedRecipeFromResult, textOf } from "./chatTraceMining.js";
import { recipeRecordFromText, recipeSignature } from "./graphRecipes.js";

export const ORG_RECIPES_STORAGE_PREFIX = "origin-recipes-org-v1";
// Saves read per sync; the gateway's own ceiling on search rows is 200.
export const ORG_FETCH_CAP = 50;
export const ORG_LOOKBACK_DAYS = 7;
// How far back the weekly walk goes; the search entity's own span ceiling.
export const ORG_MAX_LOOKBACK_DAYS = 30;
export const ORG_SYNC_SLACK_MS = 2 * 24 * 3600 * 1000;
export const ORG_SYNC_INTERVAL_MS = 60 * 60 * 1000;
export const ORG_SYNC_RETRY_MS = 10 * 60 * 1000;
export const ORG_QUERY_TIMEOUT_MS = 30_000;
export const MAX_ORG_RECIPES = 60;
// The literal a save result carries at its tail; longer than the bare tool
// name, so the index's prefilter passes fewer blocks and the scan returns
// well inside the gateway's deadline.
export const SAVE_NEEDLE = '"tool_name":"save_recipe"';
// The literal a run_recipe result carries; its text ends with the browser's
// record of the recipe (graphRecipes.recipeRecordLine).
export const RECORD_NEEDLE = '"tool_name":"run_recipe"';
export const ORG_RECORDS_CAP = 200;
export const MAX_RECORD_BROWSERS = 50;
const CONTENT_READ_LOOKBACK_MS = 90 * 24 * 3600 * 1000;

async function gatewayClients() {
    const [analytics, content, inventory] = await Promise.all([import("./analyticsClient.js"), import("./conversationContent.js"), import("./inventoryRows.js")]);
    return {
        query: analytics.runQuery,
        readContent: content.readConversationContent,
        toBlock: content.conversationPartToBlock,
        toBytesId: inventory.uuidToEndpointId,
        maxRefs: content.MAX_CONTENT_REFERENCES,
    };
}

export function rowsToObjects({ columns = [], rows = [] } = {}) {
    return rows.map(({ values = [] } = {}) => Object.fromEntries(columns.map((c, i) => [c, values[i]])));
}

const S = (name) => ({ stepAlias: "s", field: `DIMENSION_FIELD_CONVERSATION_SEARCH_${name}` });
const SEARCH_ROOT = { alias: "s", entity: "ENTITY_TYPE_CONVERSATION_SEARCH_CONTENT" };
const dims = (pairs) => pairs.map(([field, outputAlias]) => ({ field, outputAlias }));
const HIT_DIMS = [[S("TRACE_ID"), "trace_id"], [S("MESSAGE_ID"), "message_id"], [S("SOURCE_EVENT_ID"), "source_event_id"], [S("PART_ORDINAL"), "part_ordinal"], [S("CHUNK_ORDINAL"), "chunk"], [S("EVENT_TIME"), "event_time"], [S("ENDPOINT_ID"), "endpoint_id"], [S("TEXT"), "text"]];

// The window a sync scans: the lookback for a fresh cache, else from the
// last sync less the slack, never wider than the lookback.
export function syncWindow({ now = Date.now(), watermark = null, lookbackDays = ORG_LOOKBACK_DAYS, slackMs = ORG_SYNC_SLACK_MS } = {}) {
    const floor = now - lookbackDays * 24 * 3600 * 1000;
    const since = Number.isFinite(watermark) ? Math.max(floor, watermark - slackMs) : floor;
    return { start: new Date(since).toISOString(), end: new Date(now).toISOString() };
}

// The next week back from what the cache has covered, or null once a month
// is covered (or nothing has been read forward yet).
export function backWindow({ now = Date.now(), coveredFrom = null, lookbackDays = ORG_LOOKBACK_DAYS, maxLookbackDays = ORG_MAX_LOOKBACK_DAYS } = {}) {
    if (!Number.isFinite(coveredFrom)) return null;
    const floor = now - maxLookbackDays * 24 * 3600 * 1000;
    if (coveredFrom - floor < 3600 * 1000) return null;
    const start = Math.max(floor, coveredFrom - lookbackDays * 24 * 3600 * 1000);
    return { start: new Date(start).toISOString(), end: new Date(coveredFrom).toISOString() };
}

function resultSearchSpec({ needle, window, limit }) {
    return {
        root: SEARCH_ROOT,
        timeRange: { absolute: { start: window.start, end: window.end } },
        // A case-sensitive LIKE keeps the index's prefilter; ILIKE would scan.
        filter: { andExpr: { children: [
            { pred: { field: S("TEXT"), op: "OPERATOR_LIKE", value: `%${needle}%` } },
            { pred: { field: S("PART_KIND"), op: "OPERATOR_EQ", value: "tool_result" } },
        ] } },
        output: { dimensions: dims(HIT_DIMS) },
        orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_DESC" }],
        limit,
    };
}

export function savesSearchSpec({ window, limit = ORG_FETCH_CAP }) {
    return resultSearchSpec({ needle: SAVE_NEEDLE, window, limit });
}

export function recordsSearchSpec({ window, limit = ORG_RECORDS_CAP }) {
    return resultSearchSpec({ needle: RECORD_NEEDLE, window, limit });
}

// The record a run_recipe result carries, from a hit's text: the wrapped
// result when the part fit one chunk, or a bare later chunk, since the
// record line sits at the end of the result beside the tool name the
// search matched on.
export function recordFromResultText(text, row = {}) {
    const raw = String(text || "");
    let inner = raw;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
            if (parsed.tool_name && parsed.tool_name !== "run_recipe") return null;
            if (parsed.is_error) return null;
            inner = textOf(parsed.content);
        }
    } catch { /* a later chunk: the tag is still in the raw text */ }
    const record = recipeRecordFromText(inner) || recipeRecordFromText(raw);
    if (!record) return null;
    return { ...record, traceId: row.trace_id || null, at: record.at ?? parseTimestamp(row.event_time) };
}

export function recordsFromHits(rows) {
    const out = [];
    for (const row of rows || []) { const r = row.text ? recordFromResultText(row.text, row) : null; if (r) out.push(r); }
    return out;
}

// The org's record of one recipe: the latest record from each browser,
// summed, with the saving averaged by the runs behind it.
export function aggregateRecords(byBrowser) {
    const records = Object.values(byBrowser || {});
    if (!records.length) return null;
    let uses = 0, successes = 0, weighted = 0, weight = 0, lastUsedAt = null;
    for (const r of records) {
        uses += r.uses || 0;
        successes += r.successes || 0;
        if (Number.isFinite(r.savedAvg) && r.uses) { weighted += r.savedAvg * r.uses; weight += r.uses; }
        if (r.at && (!lastUsedAt || r.at > lastUsedAt)) lastUsedAt = r.at;
    }
    return { browsers: records.length, uses, successes, savedAvg: weight ? weighted / weight : null, lastUsedAt };
}

// Only what a recipe is, not the saving browser's view of it: the store's
// usage, confidence and status ride along in the saved JSON and are that
// browser's, not the org's.
const RECIPE_FIELDS = ["id", "name", "when", "source", "version", "params", "steps", "example", "examples", "paraphrases", "origin", "yields", "createdAt", "described"];
export function pickRecipe(saved) {
    const out = {};
    for (const k of RECIPE_FIELDS) if (saved?.[k] !== undefined) out[k] = saved[k];
    return out;
}

function parseTimestamp(value) {
    if (value == null || value === "") return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const s = String(value);
    // The gateway writes "YYYY-MM-DD HH:MM:SS.mmm" without a zone: it is UTC.
    const iso = /^\d{4}-\d\d-\d\d \d\d:\d\d:\d\d/.test(s) ? `${s.replace(" ", "T")}Z` : s;
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : null;
}

// A save from a search hit's own text, when the whole result fit one chunk.
// The indexed text of a tool_result part is the result as the transcript
// holds it: content blocks, error flag, tool name, call id.
export function saveFromResultText(text, row = {}) {
    let parsed = null;
    try { parsed = JSON.parse(String(text || "")); } catch { return null; }
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.tool_name && parsed.tool_name !== "save_recipe") return null;
    if (parsed.is_error) return null;
    const saved = savedRecipeFromResult(textOf(parsed.content));
    if (!saved) return null;
    return saveFromRecipe(saved.recipe, row, saved.outcome);
}

function saveFromRecipe(recipe, row, outcome = null) {
    if (!recipe?.id || !Array.isArray(recipe.steps) || !recipe.steps.length) return null;
    const savedAt = parseTimestamp(recipe.createdAt) ?? parseTimestamp(row.event_time);
    return {
        recipe: pickRecipe(recipe),
        provenance: { traceId: row.trace_id || null, endpointId: row.endpoint_id || null, savedAt, chatStartedAt: parseTimestamp(row.event_time), outcome },
    };
}

function timeout(ms, what) {
    return new Promise((_, reject) => setTimeout(() => reject(new Error(`${what} took longer than ${Math.round(ms / 1000)}s`)), ms));
}

// Hits whose text did not carry the whole result (a part over one chunk, or
// the text withheld) are read through the content API, per trace, in
// batches of at most the API's reference cap.
export async function readSavesWhole(rows, { readContent, toBlock, toBytesId = (id) => id, maxRefs = 256, now = Date.now() }) {
    const out = [];
    const byTrace = new Map();
    for (const row of rows) { if (!row.trace_id || !row.source_event_id || !row.message_id) continue; const list = byTrace.get(row.trace_id) || []; list.push(row); byTrace.set(row.trace_id, list); }
    const timeRange = { absolute: { start: new Date(now - CONTENT_READ_LOOKBACK_MS).toISOString(), end: new Date(now).toISOString() } };
    for (const [traceId, list] of byTrace) {
        for (let i = 0; i < list.length; i += maxRefs) {
            const batch = list.slice(i, i + maxRefs);
            let contents = [];
            try {
                contents = await readContent({ traceId: toBytesId(traceId), timeRange, references: batch.map((row) => ({ messagePart: { sourceEventId: toBytesId(row.source_event_id), messageId: toBytesId(row.message_id), ordinal: Number(row.part_ordinal) || 0 } })) });
            } catch { continue; }
            batch.forEach((row, j) => {
                try {
                    const block = toBlock(contents[j]?.messagePart);
                    if (!block || block.type !== "tool_result" || (block.tool_name && block.tool_name !== "save_recipe") || block.is_error) return;
                    const saved = savedRecipeFromResult(textOf(block.content));
                    const entry = saved ? saveFromRecipe(saved.recipe, row, saved.outcome) : null;
                    if (entry) out.push(entry);
                } catch { /* a part the reader cannot shape is not a save */ }
            });
        }
    }
    return out;
}

// What a search for saves yields: the saves parsed from hits' text, and the
// hits that need reading whole.
export function savesFromHits(rows) {
    const saves = [];
    const unread = [];
    for (const row of rows || []) {
        if (row.chunk != null && Number(row.chunk) > 0) { unread.push(row); continue; }
        const entry = row.text ? saveFromResultText(row.text, row) : null;
        if (entry) saves.push(entry);
        else if (!row.text || looksLikeSave(row.text)) unread.push(row);
    }
    return { saves, unread };
}

// A tool_result whose text names the tool but did not parse is a save cut
// by a chunk boundary; a result of another tool that merely mentions the
// name is not.
function looksLikeSave(text) {
    const s = String(text || "");
    return /"tool_name"\s*:\s*"save_recipe"/.test(s) || /\\"saved\\"\s*:\s*\{/.test(s.slice(0, 200));
}

// The browser cache: recipes by id with provenance, and when the last sync
// ran. Browser-local like the recipe store, keyed by tenant and user; a
// browser with no verified identity has no cache.
export function createOrgRecipeCache({ storage = null, keyFor = () => browserStorageKey(ORG_RECIPES_STORAGE_PREFIX), now = () => Date.now() } = {}) {
    function resolveStorage() {
        if (storage) return storage;
        try { return globalThis.localStorage || null; } catch { return null; }
    }
    const empty = () => ({ recipes: {}, records: {}, lastSyncAt: null, lastAttemptAt: null, lastError: null, lastRead: null, coveredFrom: null });
    function read() {
        const key = keyFor();
        const s = resolveStorage();
        if (!key || !s) return empty();
        try {
            const raw = s.getItem(key);
            const data = raw ? JSON.parse(raw) : null;
            if (!data || typeof data !== "object") return empty();
            return { ...empty(), ...data, recipes: data.recipes && typeof data.recipes === "object" ? data.recipes : {}, records: data.records && typeof data.records === "object" ? data.records : {} };
        } catch { return empty(); }
    }
    let lastWriteError = null;
    function write(data) {
        const key = keyFor();
        const s = resolveStorage();
        if (!key || !s) { lastWriteError = "no browser storage"; return false; }
        const attempt = (d) => { s.setItem(key, JSON.stringify({ version: 1, savedAt: now(), ...d })); lastWriteError = null; return true; };
        try { return attempt(data); } catch (err) {
            // The origin's quota is shared with the investigation graphs:
            // keep the newest half of the recipes and try once more.
            try {
                const ids = Object.keys(data.recipes).sort((a, b) => (data.recipes[b].provenance?.savedAt || 0) - (data.recipes[a].provenance?.savedAt || 0));
                const kept = Object.fromEntries(ids.slice(0, Math.max(1, Math.floor(ids.length / 2))).map((id) => [id, data.recipes[id]]));
                return attempt({ ...data, recipes: kept });
            } catch (err2) {
                lastWriteError = `browser storage refused the write (${(err2 || err)?.name || "error"})`;
                return false;
            }
        }
    }
    function view(entry, variants = 1, data = null) {
        return { ...entry.recipe, source: "org", org: { ...entry.provenance, variants, record: data ? aggregateRecords(data.records[entry.recipe.id]) : null } };
    }
    // One recipe per plan signature: the newest save stands for the rest.
    function collapsed(data) {
        const groups = new Map();
        for (const e of Object.values(data.recipes)) {
            let sig = "-";
            try { sig = recipeSignature(e.recipe); } catch { sig = `id:${e.recipe.id}`; }
            const g = groups.get(sig) || [];
            g.push(e);
            groups.set(sig, g);
        }
        const out = [];
        for (const g of groups.values()) {
            g.sort((a, b) => (b.provenance?.savedAt || 0) - (a.provenance?.savedAt || 0));
            out.push(view(g[0], g.length, data));
        }
        return out.sort((a, b) => (b.org.savedAt || 0) - (a.org.savedAt || 0));
    }
    return {
        available() { return Boolean(keyFor() && resolveStorage()); },
        list() { return collapsed(read()); },
        get(id) {
            const data = read();
            const e = data.recipes[id];
            return e ? view(e, 1, data) : null;
        },
        // The org's record of a recipe by id, whether or not the recipe
        // itself is cached (a local recipe the org has run).
        recordOf(id) { return aggregateRecords(read().records[id]); },
        // Saves merge by recipe id, the newest save of an id winning (a
        // generalized recipe is saved again whole). A forward read moves the
        // sync time; a read further back only extends what is covered.
        merge({ saves = [], records = [], exclude = null, at = now(), window = null, back = false } = {}) {
            const data = read();
            let added = 0, updated = 0, recorded = 0;
            // Records: the latest from each browser, this browser's own left
            // out, at most so many browsers per recipe.
            for (const r of records) {
                if (!r?.id || !r?.browser || (exclude && r.browser === exclude)) continue;
                const byBrowser = data.records[r.id] || {};
                const prev = byBrowser[r.browser];
                if (prev && (prev.at || 0) >= (r.at || 0)) continue;
                if (!prev && Object.keys(byBrowser).length >= MAX_RECORD_BROWSERS) continue;
                byBrowser[r.browser] = { uses: r.uses || 0, successes: r.successes || 0, savedAvg: Number.isFinite(r.savedAvg) ? r.savedAvg : null, last: r.last || null, at: r.at || at };
                data.records[r.id] = byBrowser;
                recorded++;
            }
            for (const s of saves) {
                if (!s?.recipe?.id) continue;
                const prev = data.recipes[s.recipe.id];
                if (prev && (prev.provenance?.savedAt || 0) >= (s.provenance?.savedAt || 0)) continue;
                data.recipes[s.recipe.id] = { recipe: s.recipe, provenance: { ...s.provenance }, firstSeenAt: prev?.firstSeenAt || at };
                if (prev) updated++; else added++;
            }
            const ids = Object.keys(data.recipes);
            if (ids.length > MAX_ORG_RECIPES) {
                ids.sort((a, b) => (data.recipes[b].provenance?.savedAt || 0) - (data.recipes[a].provenance?.savedAt || 0));
                for (const id of ids.slice(MAX_ORG_RECIPES)) delete data.recipes[id];
            }
            const start = window?.start ? Date.parse(window.start) : NaN;
            if (Number.isFinite(start)) data.coveredFrom = Number.isFinite(data.coveredFrom) ? Math.min(data.coveredFrom, start) : start;
            if (!back) {
                data.lastSyncAt = at;
                data.lastAttemptAt = at;
                data.lastError = null;
                data.lastRead = { saves: saves.length, added, updated, records: recorded };
            }
            const ok = write(data);
            return { added, updated, recorded, written: ok, error: ok ? null : lastWriteError };
        },
        noteFailure(message, at = now()) {
            const data = read();
            data.lastAttemptAt = at;
            data.lastError = String(message || "failed").slice(0, 300);
            write(data);
        },
        // A sync is due when none has run, the interval has passed since the
        // last one, or the last attempt failed long enough ago to retry.
        due({ at = now(), intervalMs = ORG_SYNC_INTERVAL_MS, retryMs = ORG_SYNC_RETRY_MS } = {}) {
            const data = read();
            const last = data.lastAttemptAt || data.lastSyncAt;
            if (!last) return true;
            return at - last >= (data.lastError ? retryMs : intervalMs);
        },
        status() {
            const data = read();
            const at = now();
            return {
                count: Object.keys(data.recipes).length,
                plans: collapsed(data).length,
                recorded: Object.keys(data.records).length,
                lastSyncAt: data.lastSyncAt, lastAttemptAt: data.lastAttemptAt, lastError: data.lastError, lastRead: data.lastRead,
                watermark: data.lastSyncAt,
                coveredFrom: data.coveredFrom,
                coveredDays: Number.isFinite(data.coveredFrom) ? Math.max(1, Math.round((at - data.coveredFrom) / (24 * 3600 * 1000))) : 0,
            };
        },
        clear() {
            const key = keyFor();
            const s = resolveStorage();
            if (key && s) { try { s.removeItem(key); } catch { /* best effort */ } }
        },
    };
}

// One sync per browser at a time.
let inflight = null;

// Read the org's saves since the last sync into the cache. Never
// throws: a failure is recorded on the cache and reported in the result.
// `clients` replaces the gateway clients (tests); without it they are
// loaded on the first sync.
// `browserId` is this browser's own, so its records are left out of what it
// reads back; `records: false` skips the records read (a surface with no
// store of its own).
export async function syncOrgRecipes({ cache, clients = null, now = Date.now, force = false, signal = null, timeoutMs = ORG_QUERY_TIMEOUT_MS, cap = ORG_FETCH_CAP, recordsCap = ORG_RECORDS_CAP, browserId = null, records = true, log = () => {} } = {}) {
    if (!cache?.available?.()) return { skipped: "no browser storage", changed: false };
    if (!force && !cache.due({ at: now() })) return { skipped: "synced recently", changed: false };
    if (inflight) return inflight;
    inflight = (async () => {
        const at = now();
        const window = syncWindow({ now: at, watermark: cache.status().watermark });
        try {
            const { query, ...content } = clients || await gatewayClients();
            const run = (spec, what) => Promise.race([query(spec, { cache: false, signal }), timeout(timeoutMs, what)]);
            const hits = rowsToObjects(await run(savesSearchSpec({ window, limit: cap }), "reading the org's saved recipes"));
            const { saves, unread } = savesFromHits(hits);
            if (unread.length) saves.push(...await readSavesWhole(unread, { ...content, now: at }));
            const merged = cache.merge({ saves, at, window });
            const out = { ...merged, saves: saves.length, hits: hits.length, window, changed: merged.added > 0 || merged.updated > 0, error: merged.error, back: null, records: null };
            // The records other browsers published with their runs. A failed
            // read here loses nothing the saves read found.
            const readRecords = async (win) => {
                const rows = rowsToObjects(await run(recordsSearchSpec({ window: win, limit: recordsCap }), "reading the org's recipe records"));
                return recordsFromHits(rows);
            };
            if (records) {
                try {
                    const found = await readRecords(window);
                    const kept = cache.merge({ records: found, exclude: browserId, at, window, back: true });
                    out.records = { read: found.length, recorded: kept.recorded, error: kept.error };
                    out.changed = out.changed || kept.recorded > 0;
                } catch (err) {
                    log("org recipe records not read:", err);
                    out.records = { error: String(err?.message || err) };
                }
            }
            // One more week back than the cache has covered, until a month is.
            const older = backWindow({ now: at, coveredFrom: cache.status().coveredFrom });
            if (older) {
                try {
                    const backHits = rowsToObjects(await run(savesSearchSpec({ window: older, limit: cap }), "reading the org's older recipes"));
                    const sorted = savesFromHits(backHits);
                    if (sorted.unread.length) sorted.saves.push(...await readSavesWhole(sorted.unread, { ...content, now: at }));
                    const olderRecords = records ? await readRecords(older).catch((err) => { log("org recipe records further back not read:", err); return []; }) : [];
                    const backMerged = cache.merge({ saves: sorted.saves, records: olderRecords, exclude: browserId, at, window: older, back: true });
                    out.back = { window: older, saves: sorted.saves.length, added: backMerged.added, updated: backMerged.updated, recorded: backMerged.recorded, error: backMerged.error };
                    out.changed = out.changed || backMerged.added > 0 || backMerged.updated > 0 || backMerged.recorded > 0;
                } catch (err) {
                    log("org recipes further back not read:", err);
                    out.back = { window: older, error: String(err?.message || err) };
                }
            }
            return out;
        } catch (err) {
            const message = String(err?.message || err);
            cache.noteFailure(message, at);
            log("org recipes not read:", err);
            return { error: message, changed: false, window };
        } finally { inflight = null; }
    })();
    return inflight;
}

export function _resetOrgRecipeSyncForTests() { inflight = null; }
